import { Component, Fragment, useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { withTranslation } from "react-i18next";
import type { WithTranslation } from "react-i18next";

import { NewspaperIcon, MessageSquareIcon, PlusIcon } from "@lucide/icons";
import type { TFunction } from "i18next";

import { OpenAPI } from "simplydo/interfaces";
import { Emoji } from "simplydo/react/components/Choosers/EmojiChooser/Emoji";
import { EmojiChooser } from "simplydo/react/components/Choosers/EmojiChooser/EmojiChooser";
import { GiphyGif } from "simplydo/react/components/Giphy/Gif";
import { RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import {
  type DiscussionPoll,
  DiscussionPolls,
} from "components/discussions/Discussion/DiscussionPolls";

import type {
  FeedCardSharedProps,
  FeedCommentMediaItem,
  FeedCommentReact,
  FeedGroupDiscussionItem,
} from "../types";
import FeedCard from "./FeedCard";

const EmojiLabel = styled(Label)`
  && {
    padding: 2px 4px 1px 4px;
    max-width: none !important;
    cursor: pointer;

    &:hover {
      filter: brightness(1.05);
    }
  }
`;

const NewEmojiLabel = styled(Label)`
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s;
  filter: grayscale(100%);

  &:hover {
    opacity: 1;
    filter: grayscale(0%);
  }
`;

const buildDiscussionLink = ({
  groupId,
  discussionId,
  commentId,
  openReply = false,
}: {
  groupId: string;
  discussionId: string;
  commentId: string;
  openReply?: boolean;
}) => {
  const params = new URLSearchParams();
  params.set("comment", commentId);
  if (openReply) {
    params.set("reply", "1");
  }
  return `/groups/${groupId}/discussions/${discussionId}?${params.toString()}`;
};

type GroupedReact = {
  emoji: string;
  count: number;
  users: (OpenAPI.Schemas["PreparedUser"] | undefined)[];
  hasReacted: boolean;
};

const groupReacts = (
  reacts: FeedCommentReact[] | undefined,
  userId: string,
): GroupedReact[] => {
  const grouped: GroupedReact[] = [];
  (reacts || []).forEach((react) => {
    let index = grouped.findIndex((item) => item.emoji === react.emoji);
    if (index === -1) {
      grouped.push({
        emoji: react.emoji,
        count: 0,
        users: [],
        hasReacted: false,
      });
      index = grouped.length - 1;
    }
    const target = grouped[index];
    if (!target) {
      return;
    }
    target.users.push(react.ownerUser);
    target.count += 1;
    if (react.user === userId) {
      target.hasReacted = true;
    }
  });
  return grouped;
};

type DiscussionFeedReactsProps = {
  comment: FeedGroupDiscussionItem;
  user: OpenAPI.Schemas["PreparedUser"];
  canUseReacts: boolean;
};

const DiscussionFeedReacts = ({
  comment,
  user,
  canUseReacts,
}: DiscussionFeedReactsProps) => {
  const [reacts, setReacts] = useState(comment.reacts || []);
  const groupedReacts = useMemo(
    () => groupReacts(reacts, user._id),
    [reacts, user._id],
  );

  const reactComment = useCallback(
    (emoji: string, adding: boolean) => {
      const reactUpdater = adding
        ? api.discussions.commentAddReaction
        : api.discussions.commentRemoveReaction;
      reactUpdater(
        comment.discussion,
        comment._id,
        emoji,
        (data) => setReacts(data.reacts || []),
        (err) => toast.error(err.message),
      );
    },
    [comment._id, comment.discussion],
  );

  if (!canUseReacts) {
    return null;
  }

  return (
    <>
      {groupedReacts.map((react) => (
        <Popup
          key={react.emoji}
          trigger={
            <EmojiLabel
              size="small"
              onClick={() => {
                reactComment(react.emoji, !react.hasReacted);
              }}
              color={react.hasReacted ? "yellow" : "grey"}
            >
              <Emoji
                style={{ display: "inline-block" }}
                emoji={{ id: react.emoji }}
                size={14}
              />
              <span
                style={{
                  verticalAlign: "top",
                  display: "inline-block",
                  marginLeft: 5,
                  marginTop: 2,
                }}
              >
                {react.count}
              </span>
            </EmojiLabel>
          }
          content={
            <p style={{ fontSize: 11 }}>
              {`${react.users.map((reactUser) => (reactUser?._id === user._id ? "You" : reactUser?.profile?.fullName || "Someone")).join(" and ")} reacted with :${react.emoji}:`}
            </p>
          }
        />
      ))}
      <Popup
        fluid
        on="click"
        transparent
        size="small"
        position="top right"
        trigger={
          <NewEmojiLabel
            size="small"
            style={{
              padding: "2px 4px 1px 4px",
              maxWidth: "none",
              cursor: "pointer",
            }}
            color="grey"
          >
            <Emoji
              style={{ display: "inline-block" }}
              emoji={{ id: "grinning" }}
              size={14}
            />
            <Icon
              icon={PlusIcon}
              style={{
                verticalAlign: "top",
                marginRight: 0,
                marginLeft: 4,
                marginTop: 2,
              }}
            />
          </NewEmojiLabel>
        }
        content={
          <EmojiChooser onComplete={(emoji) => reactComment(emoji, true)} />
        }
      />
    </>
  );
};

type DiscussionFeedPollsProps = {
  comment: FeedGroupDiscussionItem;
  user: OpenAPI.Schemas["PreparedUser"];
};

const DiscussionFeedPolls = ({ comment, user }: DiscussionFeedPollsProps) => {
  const [polls, setPolls] = useState<DiscussionPoll[]>(comment.polls || []);

  const voteOnPoll = useCallback(
    (commentId: string, pollId: string, optionIds: string[]) =>
      new Promise<void>((resolve, reject) => {
        api.discussions.voteOnCommentPoll(
          comment.discussion,
          commentId,
          pollId,
          { optionIds },
          (data) => {
            setPolls((prev) =>
              prev.map((poll) => (poll._id === pollId ? data.poll : poll)),
            );
            resolve();
          },
          (err) => {
            toast.error(err.message);
            reject(err);
          },
        );
      }),
    [comment.discussion],
  );

  const setPollClosed = useCallback(
    (commentId: string, pollId: string, closed: boolean) =>
      new Promise<void>((resolve, reject) => {
        api.discussions.editComment(
          comment.discussion,
          commentId,
          { [closed ? "closePollId" : "reopenPollId"]: pollId },
          () => {
            setPolls((prev) =>
              prev.map((poll) =>
                poll._id === pollId ? { ...poll, closed } : poll,
              ),
            );
            resolve();
          },
          (err) => {
            toast.error(err.message);
            reject(err);
          },
        );
      }),
    [comment.discussion],
  );

  if (comment.parent || !polls?.length) {
    return null;
  }

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <DiscussionPolls
        commentId={comment._id}
        polls={polls}
        disabled={!!comment.requiresApproval}
        voteOnPoll={voteOnPoll}
        setPollClosed={setPollClosed}
        canClosePoll={comment.user === user._id && !comment.requiresApproval}
      />
    </div>
  );
};

type CardLeftProps = { comment: FeedGroupDiscussionItem };

const CardLeft = ({ comment }: CardLeftProps) => {
  const theme = useTheme();
  const commentHtml = comment.messageHtml || comment.message || "";
  const hasComment = !!(commentHtml || comment.messageText);
  const hasMedia = comment.media?.length;
  const firstMediaItem: FeedCommentMediaItem =
    hasMedia && comment.media ? (comment.media[0] ?? {}) : {};
  const firstMediaItemIsImage =
    firstMediaItem.type === "gif" || firstMediaItem.type === "image";
  const firstMediaIsGif = firstMediaItem.type === "gif";
  const richTextValue =
    commentHtml || (comment.messageText ? `<p>${comment.messageText}</p>` : "");

  return (
    <div className="comment-column">
      <div className="comment-column-container">
        <Icon
          style={{
            color: theme.primaryColour,
            marginRight: 15,
            fontSize: "3em",
          }}
          icon={NewspaperIcon}
          size="large"
        />
        {!hasComment && hasMedia && firstMediaItemIsImage ? (
          firstMediaIsGif ? (
            <GiphyGif gif={firstMediaItem.data} size="fixed" />
          ) : (
            <img
              style={{ borderRadius: 5, objectFit: "contain", maxHeight: 150 }}
              alt={firstMediaItem.value}
              src={firstMediaItem.signedDownloadRequest || firstMediaItem.value}
            />
          )
        ) : null}
        {hasComment ? (
          <div style={{ maxHeight: "100%", flex: 1 }}>
            <RichTextViewer value={richTextValue} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

type GroupDiscussionFeedCardInnerProps = FeedCardSharedProps & {
  comment: FeedGroupDiscussionItem;
  t: TFunction;
};

const GroupDiscussionFeedCardInner = ({
  comment,
  hideAction,
  feedType,
  t,
}: GroupDiscussionFeedCardInnerProps) => {
  const user = useAppSelector((state) => state.user);
  const viewCommentId = comment._id;
  const canPostReplies = util.hasPermission(
    user,
    "discussion.postReplies",
    comment.discussion,
    comment.group,
  );
  const canUseReacts = util.hasPermission(
    user,
    "discussion.addReacts",
    comment.discussion,
    comment.group,
  );
  const linkParams = {
    groupId: comment.group,
    discussionId: comment.discussion,
  };
  const viewLink = buildDiscussionLink({
    ...linkParams,
    commentId: viewCommentId,
  });
  const replyLink = buildDiscussionLink({
    ...linkParams,
    commentId: viewCommentId,
    openReply: true,
  });
  const extraActions: {
    label: string;
    to: string;
    icon: typeof MessageSquareIcon;
  }[] = [];

  if (canPostReplies) {
    extraActions.push({
      label: !comment.replyCount
        ? t("feeds.comments.reply")
        : util.pluralise(
            comment.replyCount,
            t("feeds.comments.reply"),
            t("feeds.comments.replies"),
          ),
      to: replyLink,
      icon: MessageSquareIcon,
    });
  }

  if (!user) {
    return null;
  }

  return (
    <FeedCard
      feedType={feedType}
      user={comment.owner}
      action={
        comment.parent
          ? "activityFeed.discussion.parentAction"
          : "activityFeed.discussion.action"
      }
      date={comment.createdAt}
      subjectItem2={comment.groupName || ""}
      subjectItem={comment.discussionTitle || ""}
      toLink={viewLink}
      type="Group feed"
      cardContent={
        <Fragment>
          <CardLeft comment={comment} />
          <DiscussionFeedPolls comment={comment} user={user} />
        </Fragment>
      }
      hideAction={hideAction}
      extraActions={extraActions}
      footerExtra={
        <DiscussionFeedReacts
          comment={comment}
          user={user}
          canUseReacts={canUseReacts}
        />
      }
    />
  );
};

type GroupDiscussionFeedCardProps = FeedCardSharedProps &
  WithTranslation & { comment: FeedGroupDiscussionItem };

class GroupDiscussionFeedCard extends Component<GroupDiscussionFeedCardProps> {
  render() {
    const { comment, hideAction, feedType, t } = this.props;
    return (
      <GroupDiscussionFeedCardInner
        comment={comment}
        hideAction={hideAction}
        feedType={feedType}
        t={t}
      />
    );
  }
}

export default withTranslation()(GroupDiscussionFeedCard);
